// DTO ⇄ view model for the shipments GraphQL query + the REST write routes.
import { formatDateInput, formatDateOrDash } from '@/lib/format/date';
import { normalizeUnit, joinReferenceId, splitReferenceId, REMOVED_LINKS } from '@/lib/utils';
import {
  appendFormAttachments,
  buildShipmentFiltersInput,
  processDiscrepancyItems,
  processExistingAttachments,
  processFilterOptions,
} from '@/lib/mappers';
import { formatTextOrDash } from '@/lib/format/text';
import { formatPercent, formatQuantityOrDash, formatPercentOrDash } from '@/lib/format/number';
import { unitSymbol } from '@/lib/mappers/units';
import { formValue, appendIfPresent } from '@/lib/mappers/formValues';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import {
  shipmentStatusLabel,
  contractStatusLabel,
  declarationStatusLabel,
  verificationStatusLabel,
} from '@/config/statuses';
import { INCOMING_SHIPMENT_SORT_FIELDS } from '../constants/incomingShipments.constants';
import { showsDaysLeft } from '../utils/incomingShipmentsUtils';
import {
  FACILITY_KINDS,
  allocationPreferenceLabel,
  buildFacilityValue,
  feedstockCategoryLabel,
  parseFacilityValue,
  toAllocationPreferenceArray,
} from '@/config/formOptions';

const formatGhg = (value) => (value == null ? '-' : `${value} g CO₂e/MJ`);

// "{quantity} {unit}" per entry with a real quantity, comma-joined and parenthesized.
const formatSecondaryQuantities = (entries) => {
  const parts = entries
    .filter(([quantity]) => quantity != null)
    .map(([quantity, unit]) => `${quantity} ${unit ?? ''}`.trim());
  return parts.length ? `(${parts.join(', ')})` : '';
};

// SINGLE_COUNTING → "Single counting".

const formatAllocation = (values) =>
  Array.isArray(values) && values.length ? values.map(allocationPreferenceLabel).join(', ') : '-';

// The server ORs ShipmentSearch's keys, so one box searches all three.
const buildShipmentSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return { shipmentId: text, sentBySupplier: { companyname: text }, material: { name: text } };
};

export const buildIncomingShipmentsQuery = ({
  page,
  pageSize,
  sortBy,
  sortDir,
  search,
  extraFilters,
  ...filters
} = {}) => {
  const sortField = INCOMING_SHIPMENT_SORT_FIELDS[sortBy];
  return {
    page,
    limit: pageSize,
    // An unsupported sort field would fail the query.
    orderBy: sortField ? [{ field: sortField, direction: sortDir ?? 'asc' }] : undefined,
    search: buildShipmentSearchInput(search),
    filters: buildShipmentFiltersInput(filters, extraFilters),
  };
};

// The shipment's own unit fields now carry values, falling back to the material's.
const shipmentUnitsByType = (node) => ({
  [UNIT_TYPES.working]: {
    unit: node.workingUnit,
    amounts: {
      shipmentQuantity: node.shipmentQuantityWorking,
      receivedDeclarationQuantity: node.receivedDeclarationQuantityWorking,
      pendingQuantity: node.pendingShipmentQuantityWorking,
      availableQuantity: node.availableShipmentQuantityWorking,
      unassignedQuantity: node.unassignedContractQuantityWorking,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: node.declarationUnit,
    amounts: {
      shipmentQuantity: node.shipmentQuantityDeclaration,
      receivedDeclarationQuantity: node.receivedDeclarationQuantityDeclaration,
      pendingQuantity: node.pendingShipmentQuantityDeclaration,
      availableQuantity: node.availableShipmentQuantityDeclaration,
      unassignedQuantity: node.unassignedContractQuantityDeclaration,
    },
  },
  [UNIT_TYPES.tracking]: {
    unit: node.trackingUnit,
    amounts: {
      shipmentQuantity: node.shipmentQuantityTracking,
      receivedDeclarationQuantity: node.receivedDeclarationQuantityTracking,
      pendingQuantity: node.pendingShipmentQuantityTracking,
      availableQuantity: node.availableShipmentQuantityTracking,
      unassignedQuantity: node.unassignedContractQuantityTracking,
    },
  },
});

// One `shipments` node → a row view model; keys match the column ids.
export const processIncomingShipment = (node) => {
  const unitsByType = shipmentUnitsByType(node);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: node.id, // stable row id (getRowId)

    shipmentId: formatTextOrDash(node.shipmentId),
    materialName: formatTextOrDash(node.material?.name),
    totalContractsLinked: node.totalContractsLinked ?? '-',
    totalDeclarationsReceived: node.totalDeclarationsReceived ?? '-',
    totalDeclarationsLinked: node.totalDeclarationsLinked ?? 0,
    supplierName: formatTextOrDash(node.sentBySupplier?.companyname),
    status: shipmentStatusLabel(node.status) ?? '-',
    facilityName: formatTextOrDash(node.receivedAtSite?.siteName?.trim()),
    terminal: formatTextOrDash(node.terminal),
    vessel: formatTextOrDash(node.vessel),

    byUnit,
    unitType: defaultUnitType(byUnit),
    // The link card sums these across a bulk selection and deducts what step 2 assigns.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    // Raw amounts, so Link contracts' assign-quantity cell can cap against them.
    unassignedByUnit: {
      [UNIT_TYPES.working]: node.unassignedContractQuantityWorking ?? null,
      [UNIT_TYPES.declaration]: node.unassignedContractQuantityDeclaration ?? null,
      [UNIT_TYPES.tracking]: node.unassignedContractQuantityTracking ?? null,
    },

    ghgEmissions: formatGhg(node.ghgEmissions),
    ghgEmissionSavings: formatPercentOrDash(node.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
    allocationPreference: formatAllocation(node.preferredAllocation),

    // Both are deliver_by_date; `dateOfDischarge` is the name the spec uses.
    dateOfDischarge: formatDateOrDash(node.dateOfDischarge),
    dateOfDispatch: formatDateOrDash(node.dateOfDispatch),
    // The server does the arithmetic; which statuses show a countdown stays a display rule.
    daysLeft: showsDaysLeft(node.status) ? (node.daysLeft ?? null) : null,
    discrepancies: node.discrepancyCount ?? 0,
    discrepancyItems: processDiscrepancyItems(node.discrepancies),
  };
};

export const processShipmentFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  supplierOptions: processFilterOptions(result?.suppliers),
});

// A `ShipmentConnection` → rows + totalCount; `total` already respects search.
export const processIncomingShipmentsPage = (connection) => ({
  rows: (connection?.nodes ?? []).map(processIncomingShipment),
  totalCount: connection?.pageInfo?.total ?? 0,
});

// Shipment details ▸ Overview, header and attachments — GraphQL `shipment(id)`.
export const processIncomingShipmentDetail = (shipment) => {
  const workingUnit = unitSymbol(shipment?.workingUnit);
  const declarationUnit = unitSymbol(shipment?.declarationUnit);
  const trackingUnit = unitSymbol(shipment?.trackingUnit);
  const workingShipmentQty = shipment?.shipmentQuantityWorking;

  return {
    id: shipment?.id,
    shipmentId: formatTextOrDash(shipment?.shipmentId),
    materialName: formatTextOrDash(shipment?.material?.name),
    supplierName: formatTextOrDash(shipment?.sentBySupplier?.companyname),
    facilityName: formatTextOrDash(shipment?.receivedAtSite?.siteName),
    terminal: formatTextOrDash(shipment?.terminal),
    status: shipmentStatusLabel(shipment?.status) ?? '-',
    // The raw code too: the header's badge tone is keyed by it, not by the label.
    statusCode: shipment?.status,
    dateOfDischarge: formatDateOrDash(shipment?.dateOfDischarge),
    dateOfDispatch: formatDateOrDash(shipment?.dateOfDispatch),
    daysLeft: shipment?.daysLeft ?? null,
    discrepancyCount: shipment?.discrepancyCount ?? 0,
    discrepancies: processDiscrepancyItems(shipment?.discrepancies),
    totalContractsLinked: shipment?.totalContractsLinked ?? '-',
    totalDeclarationsReceived: shipment?.totalDeclarationsReceived ?? '-',
    totalDeclarationsLinked: shipment?.totalDeclarationsLinked ?? 0,
    declarationUnit,
    workingUnit,
    trackingUnit,
    declarationQuantities: {
      shipmentQuantity: formatQuantityOrDash(
        shipment?.shipmentQuantityDeclaration,
        declarationUnit,
      ),
      receivedDeclarationQuantity: formatQuantityOrDash(
        shipment?.receivedDeclarationQuantityDeclaration,
        declarationUnit,
      ),
      pendingQuantity: formatQuantityOrDash(
        shipment?.pendingShipmentQuantityDeclaration,
        declarationUnit,
      ),
      availableQuantity: formatQuantityOrDash(
        shipment?.availableShipmentQuantityDeclaration,
        declarationUnit,
      ),
    },
    workingQuantities: {
      shipmentQuantity: formatQuantityOrDash(workingShipmentQty, workingUnit),
      receivedDeclarationQuantity: formatQuantityOrDash(
        shipment?.receivedDeclarationQuantityWorking,
        workingUnit,
      ),
      pendingQuantity: formatQuantityOrDash(shipment?.pendingShipmentQuantityWorking, workingUnit),
      availableQuantity: formatQuantityOrDash(
        shipment?.availableShipmentQuantityWorking,
        workingUnit,
      ),
    },
    trackingQuantities: {
      shipmentQuantity: formatQuantityOrDash(shipment?.shipmentQuantityTracking, trackingUnit),
      receivedDeclarationQuantity: formatQuantityOrDash(
        shipment?.receivedDeclarationQuantityTracking,
        trackingUnit,
      ),
      pendingQuantity: formatQuantityOrDash(
        shipment?.pendingShipmentQuantityTracking,
        trackingUnit,
      ),
      availableQuantity: formatQuantityOrDash(
        shipment?.availableShipmentQuantityTracking,
        trackingUnit,
      ),
    },
    ghgEmissions: formatGhg(shipment?.ghgEmissions),
    ghgEmissionSavings: formatPercent(shipment?.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(shipment?.rawMaterialType),
    countryOfOrigin: formatTextOrDash(shipment?.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(shipment?.feedstockCategory)),
    allocationPreference: formatAllocation(shipment?.preferredAllocation),
    // The link card deducts what step 2 assigns from unassigned, which needs the number.
    rawQuantities: {
      [UNIT_TYPES.working]: {
        unit: workingUnit,
        unassigned: shipment?.unassignedContractQuantityWorking ?? null,
        unassignedFormatted: formatQuantityOrDash(
          shipment?.unassignedContractQuantityWorking,
          workingUnit,
        ),
      },
      [UNIT_TYPES.declaration]: {
        unit: declarationUnit,
        unassigned: shipment?.unassignedContractQuantityDeclaration ?? null,
        unassignedFormatted: formatQuantityOrDash(
          shipment?.unassignedContractQuantityDeclaration,
          declarationUnit,
        ),
      },
      [UNIT_TYPES.tracking]: {
        unit: trackingUnit,
        unassigned: shipment?.unassignedContractQuantityTracking ?? null,
        unassignedFormatted: formatQuantityOrDash(
          shipment?.unassignedContractQuantityTracking,
          trackingUnit,
        ),
      },
    },
    linkedContractsSummary: {
      assignedQuantity: formatQuantityOrDash(
        shipment?.assignedContractQuantityDeclaration,
        declarationUnit,
      ),
      assignedQuantitySecondary: formatSecondaryQuantities([
        [shipment?.assignedContractQuantityWorking, workingUnit],
        [shipment?.assignedContractQuantityTracking, trackingUnit],
      ]),
      unassignedQuantity: formatQuantityOrDash(
        shipment?.unassignedContractQuantityDeclaration,
        declarationUnit,
      ),
      unassignedQuantitySecondary: formatSecondaryQuantities([
        [shipment?.unassignedContractQuantityWorking, workingUnit],
        [shipment?.unassignedContractQuantityTracking, trackingUnit],
      ]),
    },
    attachments: (shipment?.attachments?.nodes ?? []).map((attachment, index) => ({
      id: attachment.id,
      fileId: attachment.id,
      serialNo: index + 1,
      name: formatTextOrDash(attachment.filename),
      lastUpdatedOn: formatDateOrDash(attachment.updatedAt ?? attachment.createdAt),
    })),
  };
};

// Shipment-side view of a contract link — REST, no GraphQL equivalent exists yet.
const processLinkedContractForShipment = (node) => {
  const contract = node.contract ?? {};
  // TODO: no tracking-unit variant of the contract's quantities exists yet.
  const unitsByType = {
    working: {
      unit: contract.workingUnit,
      amounts: {
        assignedQty: node.assignedQuantityWorking,
        contractedQuantity: contract.workingQuantity,
        shippedQuantity: contract.shippedWorkingQuantity,
        outstandingQuantity: contract.outstandingWorkingQuantity,
      },
    },
    declaration: {
      unit: contract.declarationUnit,
      amounts: {
        assignedQty: node.assignedQuantityDeclaration,
        contractedQuantity: contract.declarationQuantity,
        shippedQuantity: contract.shippedDeclarationQuantity,
        outstandingQuantity: contract.outstandingDeclarationQuantity,
      },
    },
  };
  const byUnit = buildQuantitiesByUnit(unitsByType);

  return {
    id: node.linkId,
    // The unlink route takes the contract's own id, not the link row's.
    contractDbId: contract.id,
    contractId: formatTextOrDash(contract.contractId),
    linkedShipments: formatTextOrDash((node.linkedShipmentIds ?? []).join(', ')),
    status: contractStatusLabel(contract.status) ?? '-',
    terminal: formatTextOrDash(contract.terminal),
    unitType: defaultUnitType(byUnit),
    byUnit,
    // The link wizard deducts what step 2 assigns from outstanding, which needs the number.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    ghgEmissions: formatGhg(contract.ghgEmissions),
    ghgEmissionSavings: formatPercent(contract.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(contract.rawMaterialType),
    countryOfOrigin: formatTextOrDash(contract.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(contract.feedstockCategory)),
    allocationPreference: formatAllocation(contract.preferredAllocation),
    deliverBy: formatDateOrDash(contract.deliverBy),
    discrepancies: contract.discrepancyCount ?? 0,
    discrepancyItems: processDiscrepancyItems(contract.discrepancies?.items),
  };
};

export const processLinkedContracts = (connection) =>
  (connection?.nodes ?? []).map(processLinkedContractForShipment);

// A null amount renders as a dash; the backend has no field for the ones passed null.
const processLinkedDeclarationQuantities = (node) => {
  const unitsByType = {
    working: {
      unit: unitSymbol(node.workingUnit),
      amounts: {
        assignedQty: null,
        unassignedQty: null,
        receivedQty: node.receivedQuantityWorking,
        surplusQty: node.surplusQuantityWorking,
        writeOffQty: node.writeOffQuantityWorking,
        adjustedQty: node.adjustedQuantityWorking,
        availableQty: node.availableQuantityWorking,
      },
    },
    declaration: {
      unit: unitSymbol(node.declarationUnit),
      amounts: {
        assignedQty: null,
        unassignedQty: null,
        receivedQty: node.receivedQuantityDeclaration,
        surplusQty: node.surplusQuantityDeclaration,
        writeOffQty: node.writeOffQuantityDeclaration,
        adjustedQty: node.adjustedQuantityDeclaration,
        availableQty: node.availableQuantityDeclaration,
      },
    },
    tracking: {
      unit: unitSymbol(node.trackingUnit),
      amounts: {
        assignedQty: null,
        unassignedQty: null,
        receivedQty: node.receivedQuantityTracking,
        surplusQty: node.surplusQuantityTracking,
        writeOffQty: node.writeOffQuantityTracking,
        adjustedQty: node.adjustedQuantityTracking,
        availableQty: node.availableQuantityTracking,
      },
    },
  };
  const byUnit = buildQuantitiesByUnit(unitsByType);

  return {
    unitType: defaultUnitType(byUnit),
    byUnit,
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
  };
};

// Shipment details ▸ Linked declarations — one `linkedDeclarationsForShipment` node per row.
const processLinkedDeclaration = (node, index) => ({
  id: node.id,
  referenceId: formatTextOrDash(node.referenceId),
  declarationNo: formatTextOrDash(node.declarationNo),
  dateOfIssuance: formatDateOrDash(node.dateOfIssuance),
  dateOfDispatch: formatDateOrDash(node.dateOfDispatch),
  dateOfDischarge: formatDateOrDash(node.dateOfDischarge),
  status: declarationStatusLabel(node.status) ?? '-',
  statusValue: node.status,
  ...processLinkedDeclarationQuantities(node),
  hasSurplusOrWriteOff:
    node.surplusQuantityDeclaration != null || node.writeOffQuantityDeclaration != null,
  rawSurplus: node.surplusQuantityDeclaration,
  rawWriteOff: node.writeOffQuantityDeclaration,
  ghgEmissions: formatGhg(node.ghgEmissions),
  ghgSavings: formatPercent(node.ghgEmissionSavings),
  rawMaterialType: formatTextOrDash(node.rawMaterialType),
  countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
  feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
  allocationPreference: formatAllocation(node.allocationPreference),
  verificationStatus: verificationStatusLabel(node.declarationVerificationStatus) ?? '-',
  discrepancies: node.discrepancyCount ?? 0,
  discrepancyItems: processDiscrepancyItems(node.discrepancies),
  serialNo: index + 1,
});

export const processLinkedDeclarations = (connection) =>
  (connection?.nodes ?? []).map(processLinkedDeclaration);

// A tracking unit with no factor is valid; a factor with no tracking unit is not.
const trackingUnitFields = (values) => {
  if (!values.trackingUnit) return {};
  return { trackingUnit: values.trackingUnit, densityTracking: values.trackingDensity };
};

// Keeps a null out of a controlled input.

export const buildIncomingShipmentDto = (values) => {
  const facility = parseFacilityValue(values.facilityId);
  const isBackToBack = facility.kind === FACILITY_KINDS.backToBack;

  return {
    shipmentId: joinReferenceId(values),
    // transformCreate forces this off for a back-to-back facility; match it.
    multiSiteTransfer: isBackToBack ? 'false' : values.multiSiteTransfer,
    sentBySupplierId: values.supplierId,
    // Exactly one of the two — the server 400s when neither is present.
    receivedAtSiteId: isBackToBack ? '' : facility.id,
    receivedAtBackToBackAccountId: isBackToBack ? facility.id : '',
    terminalId: isBackToBack ? '' : values.terminalId,
    materialId: values.materialId,
    shipmentQuantity: values.shipmentQuantity,
    shipmentUnit: values.shipmentQuantityUnit,
    density: values.workingDensity,
    ...trackingUnitFields(values),
    dateOfDispatch: values.dateOfDispatch,
    dateOfDischarge: values.dateOfDischarge,
    transport: values.transportName,
    vessel: values.vessel,
    ghgEmissions: values.ghgEmissions,
    ghgEmissionSavings: values.ghgEmissionSavings,
    rawMaterialType: values.rawMaterialType,
    countryOfOrigin: values.countryOfOrigin,
    feedstockCategory: values.feedstockCategory,
  };
};

export const buildIncomingShipmentFormData = (values) => {
  const formData = new FormData();
  const dto = buildIncomingShipmentDto(values);

  Object.keys(dto).forEach((key) => appendIfPresent(formData, key, dto[key]));

  // Repeated parts, not one JSON string — that is how multer builds a real array.
  (values.allocationPreference ?? []).forEach((allocation) =>
    formData.append('preferredAllocation', allocation),
  );

  appendFormAttachments(formData, values);

  return formData;
};

// ─── Edit shipment: GET detail DTO → form values ──────────────────────────────

// Create response → the new shipment's id + Shipment ID; `batch_group` is the pre-#542 shape.
export const processCreatedIncomingShipment = (dto) => {
  const data = dto?.output?.data;
  const shipment = data?.shipment;
  return {
    id: shipment?.id ?? data?.batch_group?.id ?? null,
    shipmentId: shipment?.shipmentId ?? data?.batch_group?.rsa_id ?? null,
  };
};

// Terminal/Supplier lock when a contract OR a declaration is linked — either alone is enough.
export const isIncomingShipmentLocked = (shipment) =>
  (shipment?.totalContractsLinked ?? 0) > 0 || (shipment?.totalDeclarationsLinked ?? 0) > 0;

// One `shipment(id)` node → the Edit form's values; GraphQL names the two factors apart.
export const processIncomingShipmentForm = (shipment) => ({
  ...splitReferenceId(shipment?.shipmentId),
  facilityId: shipment?.receivedAtSite?.id
    ? buildFacilityValue(FACILITY_KINDS.site, shipment.receivedAtSite.id)
    : buildFacilityValue(FACILITY_KINDS.backToBack, shipment?.backToBackAccount?.id),
  terminalId: shipment?.terminalRefId ? String(shipment.terminalRefId) : '',
  // What Save compares against, so only a terminal the user cleared is sent as a clear.
  savedTerminalId: shipment?.terminalRefId ? String(shipment.terminalRefId) : '',
  materialId: shipment?.materialId ? String(shipment.materialId) : '',
  multiSiteTransfer: shipment?.multiSiteTransfer ? 'true' : 'false',
  supplierId: shipment?.sentBySupplier?.id ? String(shipment.sentBySupplier.id) : '',
  dateOfDispatch: formatDateInput(shipment?.dateOfDispatch),
  dateOfDischarge: formatDateInput(shipment?.dateOfDischarge),
  transportName: shipment?.transport ?? '',
  vessel: shipment?.vessel ?? '',
  shipmentQuantity: formValue(shipment?.shipmentQuantity),
  // Normalised so it matches the unit dropdown's own option values.
  shipmentQuantityUnit: normalizeUnit(shipment?.shipmentUnit),
  workingDensity: formValue(shipment?.density),
  trackingUnit: normalizeUnit(shipment?.trackingUnit),
  // Paired with the unit: a factor with no unit is what the server rejects.
  trackingDensity: shipment?.trackingUnit ? formValue(shipment?.densityTracking) : '',
  ghgEmissions: formValue(shipment?.ghgEmissions),
  ghgEmissionSavings: formValue(shipment?.ghgEmissionSavings),
  rawMaterialType: shipment?.rawMaterialType ?? '',
  countryOfOrigin: shipment?.countryOfOrigin ?? '',
  feedstockCategory: shipment?.feedstockCategory ?? '',
  allocationPreference: toAllocationPreferenceArray(shipment?.preferredAllocation),
  // TODO: the linked-contract chips need a linkedContractsForShipment read of their own.
  linkedContracts: [],
  // Chips the user removes land here and are unlinked by Save, never before it.
  [REMOVED_LINKS]: [],
  attachments: [],
  existingAttachments: processExistingAttachments(shipment?.batchAttachments),
  removeFileIds: [],
});

export const buildIncomingShipmentUpdateDto = (values) => ({
  sentBySupplierId: values.supplierId,
  receivedAtSiteId: parseFacilityValue(values.facilityId).id,
  terminalId: values.terminalId,
  materialId: values.materialId,
  shipmentQuantity: values.shipmentQuantity,
  shipmentUnit: values.shipmentQuantityUnit,
  density: values.workingDensity,
  ...trackingUnitFields(values),
  dateOfDispatch: values.dateOfDispatch,
  dateOfDischarge: values.dateOfDischarge,
  transport: values.transportName,
  vessel: values.vessel,
  ghgEmissions: values.ghgEmissions,
  ghgEmissionSavings: values.ghgEmissionSavings,
  rawMaterialType: values.rawMaterialType,
  countryOfOrigin: values.countryOfOrigin,
  feedstockCategory: values.feedstockCategory,
});

export const buildIncomingShipmentUpdateFormData = (values) => {
  const formData = new FormData();
  const dto = buildIncomingShipmentUpdateDto(values);

  Object.keys(dto).forEach((key) => appendIfPresent(formData, key, dto[key]));
  // Absent means "unchanged"; empty clears — sent only when a saved terminal was removed.
  if (values.savedTerminalId && !dto.terminalId) formData.append('terminalId', '');

  (values.allocationPreference ?? []).forEach((allocation) =>
    formData.append('preferredAllocation', allocation),
  );
  appendFormAttachments(formData, values);

  // Replace semantics — an empty array would wipe the shipment's existing links.

  return formData;
};
